import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Avatar, Typography, Spin } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, CloseOutlined } from '@ant-design/icons';
import * as FaIcons from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './LandingChatbot.css';

const { TextArea } = Input;
const { Text } = Typography;

const LandingChatbot = () => {
  const { usuario } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! 👋 Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [campaignData, setCampaignData] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cargar información de la campaña
  useEffect(() => {
    if (usuario) {
      fetchCampaignData();
    }
  }, [usuario]);

  const fetchCampaignData = async () => {
    try {
      // Primero intentar buscar en campañas de lanzamiento
      try {
        const audienciaResponse = await axios.get(`https://api.mrcirp.com/public/campanas-audiencia/enlace/${usuario}`);
        if (audienciaResponse.data && audienciaResponse.data.length > 0) {
          const audienciaCampaign = audienciaResponse.data[0];
          setCampaignData(audienciaCampaign);
          // Actualizar el mensaje inicial con información de la campaña de lanzamiento
          setMessages(prev => [{
            id: 1,
            text: `¡Hola! 👋 Soy tu asistente virtual para la campaña "${audienciaCampaign.nombre_campana}". ¿En qué puedo ayudarte hoy?`,
            sender: 'bot',
            timestamp: new Date()
          }]);
          return;
        }
      } catch (audienciaError) {
        console.log("No se encontró en campañas de lanzamiento, buscando en usuarios...");
      }
      
      // Si no se encuentra en lanzamiento, buscar en usuarios (campañas de video)
      const response = await axios.get(`https://api.mrcirp.com/public/usuarios`);
      const campaigns = response.data;
      const currentCampaign = campaigns.find(campaign => campaign.usuario === usuario);
      
      if (currentCampaign) {
        setCampaignData(currentCampaign);
        // Actualizar el mensaje inicial con información de la campaña
        const campaignName = currentCampaign.nombre_campana || currentCampaign.nombrecampana || 'esta campaña';
        setMessages(prev => [{
          id: 1,
          text: `¡Hola! 👋 Soy tu asistente virtual para la campaña "${campaignName}". ¿En qué puedo ayudarte hoy?`,
          sender: 'bot',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error al cargar datos de la campaña:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Simular respuesta del chatbot (aquí puedes integrar con tu API de IA)
      const botResponse = await generateBotResponse(inputMessage);
      
      setTimeout(() => {
        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setIsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  const generateBotResponse = async (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Respuestas específicas de la campaña
    if (campaignData) {
      const campaignName = campaignData.nombre_campana || campaignData.nombrecampana || 'esta campaña';
      const campaignDesc = campaignData.descripcion || 'Información detallada disponible en la página.';
      const creatorName = campaignData.admin_nombre || campaignData.nombrecreador || 'un miembro de nuestro equipo';
      
      // Información sobre la campaña
      if (lowerMessage.includes('campaña') || lowerMessage.includes('campana') || lowerMessage.includes('sobre') || lowerMessage.includes('que trata')) {
        return `Esta campaña se llama "${campaignName}" y trata sobre: ${campaignDesc} 😊`;
      }
      
      // Información sobre el creador
      if (lowerMessage.includes('creador') || lowerMessage.includes('quien') || lowerMessage.includes('autor')) {
        return `Esta campaña fue creada por ${creatorName}. ¿Te gustaría saber más sobre la campaña? 😊`;
      }
      
      // Videos disponibles (solo para campañas de video)
      if (lowerMessage.includes('video') || lowerMessage.includes('videos') || lowerMessage.includes('contenido')) {
        if (campaignData.urlvideo || campaignData.urlvideoproducto1 || campaignData.urlnegocio1) {
          let videoInfo = "Esta campaña incluye contenido multimedia:";
          if (campaignData.urlvideo) videoInfo += "\n• Video principal de bienvenida";
          if (campaignData.urlvideoproducto1) videoInfo += "\n• Videos de revelación";
          if (campaignData.urlnegocio1) videoInfo += "\n• Videos de éxito";
          return videoInfo + "\n\n¿Te interesa ver algún contenido específico? 🎥";
        } else {
          return `Esta campaña de lanzamiento incluye contenido informativo y servicios. ¿Te gustaría saber más sobre los servicios disponibles? 📋`;
        }
      }
      
      // Información de contacto
      if (lowerMessage.includes('contacto') || lowerMessage.includes('contactar') || lowerMessage.includes('whatsapp')) {
        return `Puedes contactarnos a través de WhatsApp (disponible en esta página) o completar el formulario de contacto. ¿Te gustaría que te ayude con algo más? 📱`;
      }
      
      // Servicios (específico para campañas de lanzamiento)
      if (lowerMessage.includes('servicio') || lowerMessage.includes('servicios')) {
        if (campaignData.servicios_titulo || campaignData.servicios_descripcion) {
          return `Esta campaña ofrece: ${campaignData.servicios_titulo || 'Servicios especializados'}. ${campaignData.servicios_descripcion || 'Más información disponible en la página.'} 🎯`;
        }
      }
    }
    
    // Respuestas generales
    const responses = {
      'hola': '¡Hola! 😊 Me da mucho gusto saludarte. ¿Hay algo específico en lo que pueda ayudarte?',
      'precio': 'Los precios varían según el plan que elijas. ¿Te gustaría que te explique las opciones disponibles?',
      'informacion': '¡Por supuesto! Estoy aquí para brindarte toda la información que necesites. ¿Sobre qué tema específico te gustaría saber más?',
      'servicio': 'Ofrecemos diferentes servicios y planes. ¿Te interesa conocer más sobre algún servicio en particular?',
      'ayuda': '¡Estoy aquí para ayudarte! 😊 ¿En qué puedo asistirte específicamente?',
      'gracias': '¡De nada! 😊 Es un placer poder ayudarte. ¿Hay algo más en lo que pueda asistirte?'
    };
    
    // Buscar palabras clave en el mensaje
    for (const [keyword, response] of Object.entries(responses)) {
      if (lowerMessage.includes(keyword)) {
        return response;
      }
    }

    // Respuesta por defecto con información de la campaña
    if (campaignData) {
      const campaignName = campaignData.nombre_campana || campaignData.nombrecampana || 'esta campaña';
      return `Gracias por tu mensaje. 😊 Estoy aquí para ayudarte con información sobre la campaña "${campaignName}". ¿Hay algo específico en lo que pueda asistirte?`;
    }
    
    return "Gracias por tu mensaje. 😊 Estoy aquí para ayudarte con cualquier consulta que tengas. ¿Hay algo específico en lo que pueda asistirte?";
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Botón flotante del chatbot */}
      <div className="landing-chatbot-toggle" onClick={() => setIsOpen(true)}>
        <FaIcons.FaRobot />
      </div>

      {/* Ventana del chatbot */}
      {isOpen && (
        <div className="landing-chatbot-overlay" onClick={() => setIsOpen(false)}>
          <div className="landing-chatbot-window" onClick={(e) => e.stopPropagation()}>
            {/* Header del chatbot */}
            <div className="landing-chatbot-header">
              <div className="landing-chatbot-header-content">
                <Avatar 
                  icon={<RobotOutlined />} 
                  style={{ backgroundColor: '#165afa' }}
                />
                <div className="landing-chatbot-header-text">
                  <Text strong style={{ color: 'white' }}>Asistente Virtual</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                    En línea
                  </Text>
                </div>
              </div>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setIsOpen(false)}
                style={{ color: 'white' }}
              />
            </div>

            {/* Mensajes */}
            <div className="landing-chatbot-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`landing-chatbot-message ${message.sender}`}
                >
                  <div className="landing-chatbot-message-content">
                    <div className="landing-chatbot-message-avatar">
                      {message.sender === 'bot' ? (
                        <Avatar 
                          icon={<RobotOutlined />} 
                          size="small"
                          style={{ backgroundColor: '#165afa' }}
                        />
                      ) : (
                        <Avatar 
                          icon={<UserOutlined />} 
                          size="small"
                          style={{ backgroundColor: '#52c41a' }}
                        />
                      )}
                    </div>
                    <div className="landing-chatbot-message-bubble">
                      <Text>{message.text}</Text>
                      <div className="landing-chatbot-message-time">
                        {formatTime(message.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="landing-chatbot-message bot">
                  <div className="landing-chatbot-message-content">
                    <div className="landing-chatbot-message-avatar">
                      <Avatar 
                        icon={<RobotOutlined />} 
                        size="small"
                        style={{ backgroundColor: '#165afa' }}
                      />
                    </div>
                    <div className="landing-chatbot-message-bubble">
                      <Spin size="small" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input del chatbot */}
            <div className="landing-chatbot-input">
              <TextArea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu mensaje..."
                autoSize={{ minRows: 1, maxRows: 3 }}
                style={{ 
                  resize: 'none',
                  border: 'none',
                  boxShadow: 'none'
                }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                style={{ 
                  backgroundColor: '#165afa',
                  borderColor: '#165afa'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingChatbot;
